// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Session, updateSession, UpdateSessionApiParameter } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DynamicDropdown, { OptionObject } from "@insite/mobius/DynamicDropdown/DynamicDropdown";
import { TextField } from "@insite/mobius/index";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@rc/App/Components/Notify/Notify";
import loadCustomerTypes from "@rc/App/Handlers/ForgetUsername/loadCustomerTypes";
import { CustomAppState } from "@rc/App/Store/Reducers";
import Button from "@rc/MobiusOverrides/Button/Button";
import { xValidateOnSubmit } from "@rc/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        isLoading: state.xForgetUsername.isLoading,
        customerTypes: state.xForgetUsername.customerTypes,
        homePageLink: getHomePageUrl(state),
    };
};

const mapDispatchToProps = {
    loadCustomerTypes,
};

export type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ForgetUsernameStyles {
    container: InjectableCss;
    input: InjectableCss;
    actions: InjectableCss;
}

export const forgetUsernameStyles: ForgetUsernameStyles = {
    container: {
        css: css`
            max-width: 480px;
        `,
    },
    input: {
        css: css`
            margin-top: 20px;
        `,
    },
    actions: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 50px;
            margin-bottom: 30px;
        `,
    },
};

const styles = forgetUsernameStyles;

const formId = "forgetUsernameForm";

const ForgetUsername: FC<Props> = ({ id, isLoading, customerTypes, loadCustomerTypes, homePageLink }) => {
    const customerTypeFieldRequiredMessage = siteMessage("Custom_customerTypeFieldRequiredMessage") as string;
    const forgetUserNameSuccess = siteMessage("Custom_forgetUserNameSuccess") as string;

    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [selectedType, setSelectedType] = useState("");
    const [options, setOptions] = useState<OptionObject[]>([]);

    const [emailAddress, setEmailAddress] = useState("");
    const [lastName, setLastName] = useState("");
    const [shipToZipCode, setShipToZipCode] = useState("");
    const [billToZipCode, setBillToZipCode] = useState("");

    const [customerTypeError, setCustomerTypeError] = useState("");

    const [formVisibility, setFormVisibility] = useState({
        isLastNameVisible: false,
        isBillToVisible: false,
        isShipToVisible: false,
    });

    useEffect(() => {
        if (!isLoading && !Object.keys(customerTypes).length) {
            loadCustomerTypes();
        }
    }, []);

    useEffect(() => {
        if (Object.keys(customerTypes).length) {
            let options: OptionObject[] = [];
            options = options.concat(
                Object.keys(customerTypes)
                    .sort()
                    .map(key => ({ optionText: (customerTypes as any)[key], optionValue: key })),
            );
            setOptions(options);
        }
    }, [customerTypes]);

    useEffect(() => {
        setFormVisibility({
            isLastNameVisible: checkCustomerType(["CSR", "BAN", "CFP", "UFP"]),
            isShipToVisible: checkCustomerType(["BAN", "CFP", "UFP"]),
            isBillToVisible: !checkCustomerType(["CSR", "BAN", "CFP", "UFP"]),
        });
    }, [selectedType]);

    const listChangeHandler = (value?: string) => {
        setSelectedType(value || "");
        clearFormValidation();
    };

    const checkCustomerType = (checker: any) => {
        const data = {
            CSR: "SA",
            BAN: "BFE",
            CFP: "CFP",
            UFP: "UFP",
        } as any;
        return checker.map((item: any) => data[item]).includes(selectedType);
    };

    const emaillChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailAddress(event.target.value);
    };

    const lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(event.target.value);
    };

    const billToChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setBillToZipCode(event.target.value);
    };

    const shipToChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShipToZipCode(event.target.value);
    };

    const validateForm = () => {
        setCustomerTypeError(selectedType ? "" : customerTypeFieldRequiredMessage);

        if (
            !selectedType ||
            (formVisibility.isLastNameVisible && !lastName) ||
            (formVisibility.isBillToVisible && !billToZipCode) ||
            (formVisibility.isShipToVisible && !shipToZipCode)
        ) {
            return false;
        }

        return true;
    };

    const clearFormValidation = () => {
        setCustomerTypeError("");
    };

    const submitForm = async () => {
        const errors = xValidateOnSubmit(formId);
        if (errors.length > 0 || !validateForm()) {
            return;
        }
        setSuccessMsg("");
        setErrorMsg("");

        const params = {
            session: {
                email: emailAddress,
                resetPassword: false,
                properties: {
                    CustomerType: selectedType,
                    LastName: lastName,
                    ClinicShipToZipCode: shipToZipCode,
                    BillToZipCode: billToZipCode,
                    ResetUsername: true,
                },
            },
        } as any;

        try {
            const result = await updateSession(params);
            setSuccessMsg(forgetUserNameSuccess);
        } catch (error) {
            setErrorMsg(error?.message);
        }
    };

    return (
        <StyledWrapper {...styles.container}>
            <DynamicDropdown
                label={translate("Select Partner Type")}
                onSelectionChange={listChangeHandler}
                selected={selectedType}
                options={options}
                error={customerTypeError}
            />

            <StyledWrapper {...styles.input}>
                <TextField
                    label={translate("Email Address")}
                    value={emailAddress}
                    onChange={emaillChangeHandler}
                    type="email"
                    {...{ formId }}
                    required={true}
                />
            </StyledWrapper>

            {formVisibility.isLastNameVisible && (
                <StyledWrapper {...styles.input}>
                    <TextField
                        label={translate("Last Name")}
                        value={lastName}
                        onChange={lastNameChangeHandler}
                        type="lastName"
                        {...{ formId }}
                        required={true}
                    />
                </StyledWrapper>
            )}

            {formVisibility.isShipToVisible && (
                <StyledWrapper {...styles.input}>
                    <TextField
                        label={translate("Clinic Ship To Zip Code")}
                        value={shipToZipCode}
                        onChange={shipToChangeHandler}
                        {...{ formId }}
                        required={true}
                        type="postalCode"
                    />
                </StyledWrapper>
            )}

            {selectedType && formVisibility.isBillToVisible && (
                <StyledWrapper {...styles.input}>
                    <TextField
                        label={translate("Bill To Zip Code")}
                        value={billToZipCode}
                        onChange={billToChangeHandler}
                        {...{ formId }}
                        required={true}
                        type="postalCode"
                    />
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.actions}>
                <Button href={homePageLink} variant="secondary">
                    {translate("Finish")}
                </Button>

                <Button variant="primary" onClick={submitForm}>
                    {translate("Forgot Username")}
                </Button>
            </StyledWrapper>

            <StyledWrapper {...styles.input}>
                {errorMsg && <Notify type="danger">{errorMsg}</Notify>}
                {successMsg && <Notify type="success">{successMsg}</Notify>}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ForgetUsername),
    definition: {
        displayName: "Forget Username",
        group: "XNGAGE" as any,
        isSystem: true,
        fieldDefinitions: [],
    },
};

export default widgetModule;
